import { useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { DEMO_EMPLOYEE } from "../lib/roles";
import {
  DEMO_COMPANY_HFS,
  employmentStatusFor,
  SEED_ACCESS_REQUESTS,
  SEED_CHARGE_CODES,
  SEED_ENTRY_NOTES,
  SEED_LEAVE_BEGIN,
  SEED_POLICIES,
  SEED_TIMESHEET_LINES,
  SEED_TIMESHEETS,
} from "../data/timekeepingSeed";
import {
  canRequestCorrection,
  CERTIFICATION_TEXT_VERSION,
  dayTotal,
  eachDay,
  EMPLOYMENT_STATUS_HELP,
  formatPeriodLabel,
  hourFloorCheck,
  hoursByLaborType,
  isEditableStatus,
  isOverFloor,
  needsLateEntryReason,
  normalizeQuarterHour,
  policyForDate,
  requiredHoursForPeriod,
  sheetTotal,
  SUPERVISOR_ATTESTATION_TEXT_VERSION,
  todayIso,
  weekdayShort,
  workAuthorizationOk,
  type ChargeCode,
  type ChargeCodeAccessRequest,
  type TimesheetEntryNote,
  type TimesheetHeader,
  type TimesheetLine,
} from "../lib/timekeeping";
import { PageHeader, RequestRoutingPanel, type RoutingStep } from "../components/RequestRouting";
import { slaForQueueStep } from "../lib/helpdesk";

function timesheetRoutingSteps(sheet: TimesheetHeader): RoutingStep[] {
  const st = sheet.status;
  const drafted: RoutingStep = {
    id: "1",
    title: "Draft",
    role: "You",
    status: st === "Draft" ? "current" : "completed",
  };
  const submitted: RoutingStep = {
    id: "2",
    title: "Submitted",
    role: "You",
    status:
      st === "Draft"
        ? "upcoming"
        : st === "Submitted" || st === "Correction requested"
          ? "completed"
          : "completed",
    at: sheet.submitted_at,
    note: sheet.certification_at ? "Certified and submitted" : undefined,
  };
  const review: RoutingStep = {
    id: "3",
    title: "Supervisor review",
    role: "Jordan Ortiz",
    status:
      st === "Draft"
        ? "upcoming"
        : st === "Submitted" || st === "Correction requested"
          ? "current"
          : "completed",
    note:
      st === "Correction requested"
        ? sheet.correction_reason
          ? `Correction requested: ${sheet.correction_reason}`
          : "Correction requested"
        : undefined,
  };
  const terminal: RoutingStep = {
    id: "4",
    title: st === "Rejected" ? "Rejected" : "Approved",
    role: "Jordan Ortiz",
    status:
      st === "Approved"
        ? "completed"
        : st === "Rejected"
          ? "rejected"
          : "upcoming",
    at: sheet.decided_at,
    note: st === "Rejected" ? sheet.reject_reason : undefined,
  };
  return [drafted, submitted, review, terminal];
}
import {
  AccessRequestModal,
  ChangeNoteModal,
  ChargeCodePicker,
  LaborTypeBadge,
  LaborTypeHelpLink,
  LaborTypeHelpPanel,
  OverFloorBadge,
  StatusChip,
  SubmitAttestationModal,
  SupervisorAttestationModal,
} from "../components/timekeeping/TimekeepingWidgets";
import { ComplianceOpsPanel } from "../components/timekeeping/ComplianceOpsPanel";
function cloneLines(lines: TimesheetLine[]) {
  return lines.map((l) => ({ ...l, hours: { ...l.hours } }));
}

export function TimekeepingPage() {
  const { moduleIds, homePath, roleIds } = useAuth();
  const [params, setParams] = useSearchParams();
  const canApprove =
    roleIds.includes("ROLE-SUPERVISOR") || roleIds.includes("ROLE-PROJECT-MANAGER");
  const canOps = roleIds.some((r) =>
    [
      "ROLE-PAYROLL",
      "ROLE-HR-PAYROLL",
      "ROLE-SUPERVISOR",
      "ROLE-PROJECT-MANAGER",
      "ROLE-FINANCE-ACCOUNTING",
      "ROLE-EXECUTIVE-BI-AUDITOR",
    ].includes(r),
  );
  const tab =
    params.get("tab") === "approvals" && canApprove
      ? "approvals"
      : params.get("tab") === "ops" && canOps
        ? "ops"
        : "entry";

  const [chargeCodes] = useState(() => [...SEED_CHARGE_CODES]);
  const [sheets, setSheets] = useState<TimesheetHeader[]>(() =>
    SEED_TIMESHEETS.map((s) => ({ ...s })),
  );
  const [lines, setLines] = useState<TimesheetLine[]>(() => cloneLines(SEED_TIMESHEET_LINES));
  const [notes, setNotes] = useState<TimesheetEntryNote[]>(() => [...SEED_ENTRY_NOTES]);
  const [accessRequests, setAccessRequests] = useState<ChargeCodeAccessRequest[]>(() => [
    ...SEED_ACCESS_REQUESTS,
  ]);
  const [policies] = useState(() => [...SEED_POLICIES]);

  const mine = useMemo(
    () =>
      sheets
        .filter((t) => t.employee_id === DEMO_EMPLOYEE.id)
        .sort((a, b) => b.period_start.localeCompare(a.period_start)),
    [sheets],
  );

  const [selectedId, setSelectedId] = useState<string>(() => mine[0]?.id ?? "");
  const selected = sheets.find((s) => s.id === selectedId) ?? mine[0];
  const selectedLines = useMemo(
    () => lines.filter((l) => l.timesheet_id === selected?.id),
    [lines, selected?.id],
  );

  const [saveFlash, setSaveFlash] = useState<string | null>(null);
  const [showLaborHelp, setShowLaborHelp] = useState(false);
  const [pickingCode, setPickingCode] = useState(false);
  const [accessTarget, setAccessTarget] = useState<ChargeCode | null>(null);
  const [accessReason, setAccessReason] = useState("");
  const [selectedAccessId, setSelectedAccessId] = useState<string | null>(null);
  const [pendingChange, setPendingChange] = useState<{
    lineId: string;
    date: string;
    oldHours: number;
    newHours: number;
  } | null>(null);
  const [changeNote, setChangeNote] = useState("");
  const [showSubmit, setShowSubmit] = useState(false);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [correctionReason, setCorrectionReason] = useState("");
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approvalViewId, setApprovalViewId] = useState<string | null>(null);
  const [approveTargetId, setApproveTargetId] = useState<string | null>(null);
  const [showRevisions, setShowRevisions] = useState(false);
  const [proxyLeaveAck, setProxyLeaveAck] = useState(false);

  if (!moduleIds.includes("timekeeping")) return <Navigate to={homePath} replace />;

  const policy = selected
    ? policyForDate(policies, selected.company_id, selected.period_start)
    : policyForDate(policies, DEMO_COMPANY_HFS.id, "2026-07-27");
  const myStatus = employmentStatusFor(DEMO_EMPLOYEE.id);
  const required = policy ? requiredHoursForPeriod(policy, myStatus) : 80;
  const days = selected ? eachDay(selected.period_start, selected.period_end) : [];
  const total = sheetTotal(selectedLines);
  const floorOk = hourFloorCheck(total, required) === "ok";
  const overFloorMine = isOverFloor(total, required);
  const editable = selected ? isEditableStatus(selected.status) : false;
  const ptHours = hoursByLaborType(selectedLines, "PT");
  const lwopHours = hoursByLaborType(selectedLines, "LWOP");
  const accruedBegin = SEED_LEAVE_BEGIN.accrued_pt;
  const bankBegin = SEED_LEAVE_BEGIN.bank_pt;
  const accruedEnd = accruedBegin - ptHours; // demo: all PT draws accrued first
  const bankEnd = bankBegin;

  const delinquent = mine.find(
    (s) =>
      s.id !== selected?.id &&
      isEditableStatus(s.status) &&
      s.due_at < "2026-07-27",
  );

  function flashSaved() {
    setSaveFlash("Saved");
    window.setTimeout(() => setSaveFlash(null), 1600);
  }

  function updateSheet(id: string, patch: Partial<TimesheetHeader>) {
    setSheets((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }

  function applyHours(
    lineId: string,
    date: string,
    newHours: number,
    noteText?: string,
    reasonCode?: string,
  ) {
    const line = lines.find((l) => l.id === lineId);
    if (!line || !selected) return;
    const code = chargeCodes.find((c) => c.code === line.charge_code);
    if (code) {
      const auth = workAuthorizationOk(code, selected.employee_id, date);
      if (!auth.ok) {
        window.alert(`Cannot charge ${code.code}: ${auth.reason}`);
        return;
      }
    }
    const oldHours = Number(line.hours[date]) || 0;
    setLines((prev) =>
      prev.map((l) =>
        l.id === lineId ? { ...l, hours: { ...l.hours, [date]: newHours } } : l,
      ),
    );
    if ((noteText || reasonCode) && oldHours !== newHours) {
      setNotes((prev) => [
        {
          id: `note-${Date.now()}`,
          timesheet_id: selected.id,
          line_id: lineId,
          date,
          old_hours: oldHours,
          new_hours: newHours,
          note: noteText || reasonCode || "Revision",
          reason_code: reasonCode,
          at: new Date().toISOString(),
          by: DEMO_EMPLOYEE.name,
        },
        ...prev,
      ]);
    }
    flashSaved();
  }

  function requestHoursChange(lineId: string, date: string, raw: string) {
    if (!editable) return;
    const parsed = raw === "" ? 0 : Number(raw);
    const normalized = normalizeQuarterHour(parsed);
    if (normalized === null) {
      window.alert("Hours must be in 0.25 increments (for example 7.5 or 8).");
      return;
    }
    const line = lines.find((l) => l.id === lineId);
    if (!line) return;
    const oldHours = Number(line.hours[date]) || 0;
    if (oldHours === normalized) return;
    const late = needsLateEntryReason(policy, date, todayIso());
    const hadPriorSave = Object.prototype.hasOwnProperty.call(line.hours, date);
    if (hadPriorSave || late) {
      setPendingChange({ lineId, date, oldHours, newHours: normalized });
      setChangeNote(late && !hadPriorSave ? "Late entry — " : "");
      return;
    }
    applyHours(lineId, date, normalized);
  }

  function addAuthorizedCode(code: ChargeCode) {
    if (!selected) return;
    const asOf = selected.period_start;
    const auth = workAuthorizationOk(code, selected.employee_id, asOf);
    if (!auth.ok) {
      window.alert(`Cannot add ${code.code}: ${auth.reason}`);
      return;
    }
    const id = `${selected.id}-L${Date.now()}`;
    setLines((prev) => [
      ...prev,
      {
        id,
        timesheet_id: selected.id,
        charge_code: code.code,
        labor_type: code.labor_type,
        hours: {},
      },
    ]);
    setPickingCode(false);
    flashSaved();
  }

  function submitAccessRequest() {
    if (!accessTarget || !accessReason.trim()) return;
    const id = `tca-${Date.now()}`;
    const row: ChargeCodeAccessRequest = {
      id,
      code: accessTarget.code,
      owner: accessTarget.manager_name,
      reason: accessReason.trim(),
      status: "Submitted",
      submitted_at: new Date().toISOString(),
    };
    setAccessRequests((prev) => [row, ...prev]);
    setSelectedAccessId(id);
    setAccessTarget(null);
    setAccessReason("");
    setPickingCode(false);
  }

  function doSubmit() {
    if (!selected) return;
    const check = hourFloorCheck(total, required);
    if (check === "empty") {
      window.alert(
        myStatus === "PTV"
          ? "Part-time variable employees have no hour minimum, but you cannot submit an empty timesheet. Enter the hours you worked."
          : "Enter hours before submitting this timesheet.",
      );
      setShowSubmit(false);
      return;
    }
    if (check === "short") {
      const label = EMPLOYMENT_STATUS_HELP[myStatus].label;
      window.alert(
        `Your employment status (${myStatus} — ${label}) requires at least ${required} hours this period (you have ${total}). You may enter more than the minimum, but not less.`,
      );
      setShowSubmit(false);
      return;
    }
    for (const line of selectedLines) {
      const code = chargeCodes.find((c) => c.code === line.charge_code);
      if (!code) continue;
      for (const [day, hrs] of Object.entries(line.hours)) {
        if (!(Number(hrs) > 0)) continue;
        const auth = workAuthorizationOk(code, selected.employee_id, day);
        if (!auth.ok) {
          window.alert(`Submit blocked — ${code.code} on ${day}: ${auth.reason}`);
          setShowSubmit(false);
          return;
        }
      }
    }
    const ptTotal = hoursByLaborType(selectedLines, "PT");
    const leaveAvailable = SEED_LEAVE_BEGIN.accrued_pt + SEED_LEAVE_BEGIN.bank_pt;
    if (ptTotal > leaveAvailable + 0.001) {
      window.alert(
        `Submit blocked — PT hours (${ptTotal}) exceed leave balance + allowance (${leaveAvailable}).`,
      );
      setShowSubmit(false);
      return;
    }
    const now = new Date().toISOString();
    updateSheet(selected.id, {
      status: "Submitted",
      submitted_at: now,
      certification_at: now,
      certified_by: DEMO_EMPLOYEE.id,
      certification_text_version: policy?.certification_text_version ?? CERTIFICATION_TEXT_VERSION,
      is_proxy: false,
      proxy_reason: undefined,
      proxy_prepared_by: undefined,
    });
    setShowSubmit(false);
    flashSaved();
  }

  function requestCorrection() {
    if (!selected || !correctionReason.trim()) return;
    updateSheet(selected.id, {
      status: "Correction requested",
      correction_reason: correctionReason.trim(),
    });
    setCorrectionOpen(false);
    setCorrectionReason("");
    flashSaved();
  }

  function removeLine(lineId: string) {
    if (!editable) return;
    const line = lines.find((l) => l.id === lineId);
    if (!line) return;
    if (Object.values(line.hours).some((h) => h > 0)) {
      window.alert("Clear hours on the row before removing it, or set them to zero with a change note.");
      return;
    }
    setLines((prev) => prev.filter((l) => l.id !== lineId));
    flashSaved();
  }

  const selectedAccess = accessRequests.find((r) => r.id === selectedAccessId);
  const accessSteps: RoutingStep[] = selectedAccess
    ? [
        {
          id: "1",
          title: "Submitted",
          role: "You",
          status: "completed",
          at: selectedAccess.submitted_at,
          note: selectedAccess.reason,
        },
        {
          id: "2",
          title: "Code manager review",
          role: selectedAccess.owner,
          status: selectedAccess.status === "Submitted" ? "current" : "completed",
          at: selectedAccess.reviewed_at,
        },
        {
          id: "3",
          title: selectedAccess.status === "Rejected" ? "Rejected" : "Approved",
          role: selectedAccess.owner,
          status:
            selectedAccess.status === "Submitted"
              ? "upcoming"
              : selectedAccess.status === "Rejected"
                ? "rejected"
                : "completed",
          at: selectedAccess.reviewed_at,
          note: selectedAccess.review_note,
        },
      ]
    : [];

  const teamQueue = sheets
    .filter((s) => s.employee_id !== DEMO_EMPLOYEE.id)
    .filter(
      (s) =>
        s.status === "Submitted" ||
        s.status === "Correction requested" ||
        s.status === "Approved" ||
        s.status === "Rejected",
    )
    .sort((a, b) => b.period_start.localeCompare(a.period_start));

  const approvalSheet = sheets.find((s) => s.id === approvalViewId);
  const approvalLines = lines.filter((l) => l.timesheet_id === approvalViewId);
  const selectedNotes = notes.filter((n) => n.timesheet_id === selected?.id);

  function approveSheet(id: string) {
    const sheet = sheets.find((s) => s.id === id);
    if (!sheet) return;
    if (sheet.employee_id === DEMO_EMPLOYEE.id) {
      window.alert("Self-approval is blocked. Another supervisor must approve this timesheet.");
      setApproveTargetId(null);
      return;
    }
    const now = new Date().toISOString();
    updateSheet(id, {
      status: "Approved",
      reject_reason: undefined,
      decided_at: now,
      supervisor_attested_at: now,
      supervisor_attested_by: DEMO_EMPLOYEE.id,
      supervisor_attestation_text_version: SUPERVISOR_ATTESTATION_TEXT_VERSION,
    });
    setApproveTargetId(null);
    setApprovalViewId(null);
  }

  function prepareProxyForProlongedLeave() {
    if (!selected || !editable) return;
    if (!proxyLeaveAck) {
      window.alert(
        "Proxy timesheets are allowed only for prolonged authorized leave. Confirm the acknowledgment first.",
      );
      return;
    }
    updateSheet(selected.id, {
      is_proxy: true,
      proxy_reason: "prolonged_leave",
      proxy_prepared_by: DEMO_EMPLOYEE.id,
    });
    window.alert(
      "Proxy prepared for prolonged leave (audited). On return, the employee must submit a superseding certified timesheet.",
    );
    flashSaved();
  }

  function rejectSheet(id: string) {
    if (!rejectReason.trim()) return;
    updateSheet(id, {
      status: "Rejected",
      reject_reason: rejectReason.trim(),
      decided_at: new Date().toISOString(),
    });
    setRejectTarget(null);
    setRejectReason("");
    setApprovalViewId(null);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Timekeeping"
        subtitle={
          canApprove
            ? "Enter your time by period, or approve your team."
            : "Enter time by period. Autosaves as you go; changes to saved cells need a note."
        }
      />

      <div className="flex flex-wrap gap-2 border-b border-black/10 pb-2 items-center">
        <button
          type="button"
          onClick={() => setParams({})}
          className={`px-3 py-1.5 text-sm ${
            tab === "entry" ? "bg-navy text-white" : "bg-white border border-black/10 text-navy"
          }`}
        >
          My timesheets
        </button>
        {canApprove && (
          <button
            type="button"
            onClick={() => setParams({ tab: "approvals" })}
            className={`px-3 py-1.5 text-sm ${
              tab === "approvals" ? "bg-navy text-white" : "bg-white border border-black/10 text-navy"
            }`}
          >
            Approvals
          </button>
        )}
        {canOps && (
          <button
            type="button"
            onClick={() => setParams({ tab: "ops" })}
            className={`px-3 py-1.5 text-sm ${
              tab === "ops" ? "bg-navy text-white" : "bg-white border border-black/10 text-navy"
            }`}
          >
            Missing / late / corrections
          </button>
        )}
        {saveFlash && <span className="text-xs text-teal ml-auto">{saveFlash}</span>}
      </div>

      {tab === "ops" && canOps && <ComplianceOpsPanel sheets={sheets} roleIds={roleIds} />}

      {tab === "approvals" && canApprove && (
        <div className="grid lg:grid-cols-[280px_1fr] gap-4">
          <div className="bg-white border border-black/5 overflow-hidden">
            <div className="px-3 py-2 border-b border-black/5 text-xs uppercase tracking-wider text-slate">
              Team timesheets
            </div>
            <ul className="max-h-64 overflow-y-auto divide-y divide-black/5">
              {teamQueue.map((s) => {
                const sPolicy = policyForDate(policies, s.company_id, s.period_start);
                const sStatus = employmentStatusFor(s.employee_id);
                const sRequired = sPolicy ? requiredHoursForPeriod(sPolicy, sStatus) : null;
                const sTotal = sheetTotal(lines.filter((l) => l.timesheet_id === s.id));
                const sOver = isOverFloor(sTotal, sRequired);
                return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => setApprovalViewId(s.id)}
                    className={`w-full text-left px-3 py-2.5 text-sm hover:bg-teal-soft/40 ${
                      approvalViewId === s.id
                        ? "bg-teal-soft border-l-4 border-l-teal"
                        : "border-l-4 border-l-transparent"
                    }`}
                  >
                    <div className="font-medium text-navy">{s.employee}</div>
                    <div className="text-xs text-slate">
                      {formatPeriodLabel(s.period_start, s.period_end)}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      <StatusChip status={s.status} />
                      {sOver ? <OverFloorBadge /> : null}
                    </div>
                  </button>
                </li>
                );
              })}
            </ul>
          </div>
          <div className="bg-white border border-black/5 p-4 space-y-3">
            {!approvalSheet ? (
              <p className="text-sm text-slate">Select a timesheet to review.</p>
            ) : (
              <>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h2 className="font-semibold text-navy">{approvalSheet.employee}</h2>
                    {(() => {
                      const apStatus = employmentStatusFor(approvalSheet.employee_id);
                      const apPolicy = policyForDate(
                        policies,
                        approvalSheet.company_id,
                        approvalSheet.period_start,
                      );
                      const apRequired = apPolicy
                        ? requiredHoursForPeriod(apPolicy, apStatus)
                        : null;
                      const apTotal = sheetTotal(approvalLines);
                      const apOver = isOverFloor(apTotal, apRequired);
                      return (
                        <>
                          <p className="text-sm text-slate">
                            {formatPeriodLabel(
                              approvalSheet.period_start,
                              approvalSheet.period_end,
                            )}{" "}
                            · {apTotal} hrs total · {apStatus}
                            {apRequired != null
                              ? ` · minimum ${apRequired} hrs`
                              : " · no minimum (PTV)"}
                          </p>
                          <div className="mt-1 flex flex-wrap gap-1">
                            <StatusChip status={approvalSheet.status} />
                            {apOver ? <OverFloorBadge /> : null}
                          </div>
                        </>
                      );
                    })()}
                    {approvalSheet.correction_reason && (
                      <p className="text-sm text-slate mt-1">
                        Correction reason: {approvalSheet.correction_reason}
                      </p>
                    )}
                    {approvalSheet.reject_reason && (
                      <p className="text-sm text-red-700 mt-1">
                        Prior rejection: {approvalSheet.reject_reason}
                      </p>
                    )}
                  </div>
                </div>
                <RequestRoutingPanel
                  title="Timesheet routing"
                  steps={timesheetRoutingSteps(approvalSheet)}
                />
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[640px]">
                    <thead>
                      <tr className="bg-mist">
                        <th className="text-left px-2 py-2 sticky left-0 bg-mist z-10 min-w-[140px]">
                          Code
                        </th>
                        <th className="text-left px-2 py-2">Type</th>
                        {eachDay(approvalSheet.period_start, approvalSheet.period_end).map((d) => (
                          <th key={d} className="text-center px-1 py-2 font-medium text-slate">
                            <div>{weekdayShort(d)}</div>
                            <div className="text-[10px] font-normal">{d.slice(5)}</div>
                          </th>
                        ))}
                        <th className="text-right px-2 py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvalLines.map((l) => (
                        <tr key={l.id} className="border-t border-black/5">
                          <td className="px-2 py-1.5 sticky left-0 bg-white z-10">{l.charge_code}</td>
                          <td className="px-2 py-1.5">
                            <LaborTypeBadge type={l.labor_type} />
                          </td>
                          {eachDay(approvalSheet.period_start, approvalSheet.period_end).map((d) => (
                            <td key={d} className="text-center px-1 py-1.5">
                              {l.hours[d] ? l.hours[d] : "—"}
                            </td>
                          ))}
                          <td className="px-2 py-1.5 text-right font-medium">
                            {Object.values(l.hours).reduce((a, b) => a + b, 0)}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t border-black/10 bg-mist/40 font-medium">
                        <td className="px-2 py-2 sticky left-0 bg-mist/40 z-10" colSpan={2}>
                          Day total
                        </td>
                        {eachDay(approvalSheet.period_start, approvalSheet.period_end).map((d) => (
                          <td key={d} className="text-center px-1 py-2">
                            {dayTotal(approvalLines, d) || "—"}
                          </td>
                        ))}
                        <td className="px-2 py-2 text-right">{sheetTotal(approvalLines)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {approvalSheet.status === "Submitted" ||
                approvalSheet.status === "Correction requested" ? (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm bg-teal text-white"
                      onClick={() => setApproveTargetId(approvalSheet.id)}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 text-sm border border-red-500/40 text-red-700"
                      onClick={() => {
                        setRejectTarget(approvalSheet.id);
                        setRejectReason("");
                      }}
                    >
                      Reject
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}

      {tab === "entry" && selected && (
        <>
          {delinquent && (
            <div className="border border-gold/40 bg-gold/10 px-3 py-2 text-sm text-navy">
              Delinquent timesheet for{" "}
              {formatPeriodLabel(delinquent.period_start, delinquent.period_end)} is still{" "}
              {delinquent.status.toLowerCase()} (due {delinquent.due_at}).
            </div>
          )}

          <div className="grid lg:grid-cols-[240px_1fr] gap-4 items-start">
            <div className="bg-white border border-black/5 overflow-hidden">
              <div className="px-3 py-2 border-b border-black/5 text-xs uppercase tracking-wider text-slate">
                Periods
              </div>
              <ul className="max-h-[11.5rem] overflow-y-auto divide-y divide-black/5">
                {mine.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(s.id)}
                      className={`w-full text-left px-3 py-2.5 text-sm hover:bg-mist ${
                        selected.id === s.id ? "bg-mist border-l-2 border-l-teal" : ""
                      }`}
                    >
                      <div className="font-medium text-navy leading-snug">
                        {formatPeriodLabel(s.period_start, s.period_end)}
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <StatusChip status={s.status} />
                        <span className="text-xs text-slate">
                          {sheetTotal(lines.filter((l) => l.timesheet_id === s.id))} hrs
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-3">
              <div className="bg-white border border-black/5 p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-semibold text-navy">
                      {formatPeriodLabel(selected.period_start, selected.period_end)}
                    </h2>
                    <p className="text-sm text-slate mt-0.5">
                      Due {selected.due_at}
                      {policy
                        ? ` · ${policy.cadence} · ${myStatus}${
                            required != null
                              ? ` · minimum ${required} hrs`
                              : " · no minimum"
                          }`
                        : null}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <StatusChip status={selected.status} />
                      {overFloorMine ? <OverFloorBadge /> : null}
                      {selected.is_proxy && (
                        <span className="text-xs px-1.5 py-0.5 bg-gold/20 text-navy font-medium">
                          Proxy (prolonged leave)
                        </span>
                      )}
                      <LaborTypeHelpLink onOpen={() => setShowLaborHelp(true)} />
                      <button
                        type="button"
                        className="text-xs text-teal underline underline-offset-2"
                        onClick={() => setShowRevisions(true)}
                      >
                        Correction history ({selectedNotes.length})
                      </button>
                    </div>
                    {policy?.total_time_accounting_required && (
                      <p className="text-xs text-slate mt-2">
                        Total Time Accounting on — record all hours worked (paid or unpaid). Unpaid
                        OT code: <code>{policy.unpaid_ot_charge_code ?? "ST-UNPAID-OT"}</code>
                        {policy.require_daily_entry
                          ? ` · Daily entry required (late window ${policy.late_entry_window_days ?? 1}d)`
                          : null}
                        {selected.certification_at
                          ? ` · Certified ${selected.certification_text_version ?? ""}`
                          : null}
                      </p>
                    )}
                    {canApprove && editable && (
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                        <label className="flex items-center gap-1.5 text-slate">
                          <input
                            type="checkbox"
                            checked={proxyLeaveAck}
                            onChange={(e) => setProxyLeaveAck(e.target.checked)}
                          />
                          Prolonged authorized leave (proxy only)
                        </label>
                        <button
                          type="button"
                          className="px-2 py-1 border border-black/10 text-navy"
                          onClick={prepareProxyForProlongedLeave}
                        >
                          Prepare proxy timesheet
                        </button>
                      </div>
                    )}
                    {selected.reject_reason && (
                      <p className="text-sm text-red-700 mt-2">
                        Rejected: {selected.reject_reason}
                      </p>
                    )}
                    {selected.correction_reason && (
                      <p className="text-sm text-slate mt-2">
                        Correction: {selected.correction_reason}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-semibold text-navy">{total}</div>
                    <div
                      className={`text-xs ${
                        floorOk ? "text-teal" : "text-gold"
                      }`}
                    >
                      {required != null
                        ? overFloorMine
                          ? `above ${required} minimum`
                          : `of ${required} minimum`
                        : "no minimum (PTV)"}
                    </div>
                  </div>
                </div>

                <RequestRoutingPanel
                  title="Timesheet routing"
                  steps={timesheetRoutingSteps(selected)}
                />

                <div className="grid sm:grid-cols-2 gap-3 text-sm border border-black/5 bg-mist/60 p-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate">
                      Accrued PT (Paid Time-Off)
                    </div>
                    <div className="text-navy mt-0.5">
                      Begin {accruedBegin} → End {accruedEnd} hrs
                      {ptHours > 0 ? (
                        <span className="text-slate"> (−{ptHours} PT this period)</span>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate">
                      Bank PT (M&A)
                    </div>
                    <div className="text-navy mt-0.5">
                      Begin {bankBegin} → End {bankEnd} hrs
                      {lwopHours > 0 ? (
                        <span className="text-slate"> · LWOP {lwopHours} hrs (unpaid)</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="sm:col-span-2 text-xs text-slate">
                    Leave begin/end reflects PT hours on this timesheet (DEC-TIME-001).{" "}
                    <Link to="/app/leave" className="text-teal underline underline-offset-2">
                      Open Leave
                    </Link>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse min-w-[720px]">
                    <thead>
                      <tr className="bg-mist">
                        <th className="text-left px-2 py-2 sticky left-0 bg-mist z-10 min-w-[160px]">
                          Charge code
                        </th>
                        <th className="text-left px-2 py-2">Type</th>
                        {days.map((d) => (
                          <th key={d} className="text-center px-1 py-2 font-medium text-slate">
                            <div>{weekdayShort(d)}</div>
                            <div className="font-normal">{d.slice(5)}</div>
                          </th>
                        ))}
                        <th className="text-right px-2 py-2">Row</th>
                        {editable ? <th className="px-1 py-2" /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedLines.map((line) => (
                        <tr key={line.id} className="border-t border-black/5 align-middle">
                          <td className="px-2 py-1.5 sticky left-0 bg-white z-10">
                            <div className="font-medium text-navy">{line.charge_code}</div>
                            <div className="text-[10px] text-slate truncate max-w-[150px]">
                              {chargeCodes.find((c) => c.code === line.charge_code)?.title}
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <LaborTypeBadge type={line.labor_type} />
                          </td>
                          {days.map((d) => (
                            <td key={d} className="px-0.5 py-1">
                              <input
                                type="number"
                                min={0}
                                max={24}
                                step={0.25}
                                disabled={!editable}
                                className="w-12 mx-auto block border border-black/10 px-1 py-1 text-center disabled:bg-mist/50"
                                value={line.hours[d] ?? ""}
                                onChange={(e) =>
                                  requestHoursChange(line.id, d, e.target.value)
                                }
                              />
                            </td>
                          ))}
                          <td className="px-2 py-1.5 text-right font-medium text-navy">
                            {Object.values(line.hours).reduce((a, b) => a + (Number(b) || 0), 0)}
                          </td>
                          {editable ? (
                            <td className="px-1 py-1">
                              <button
                                type="button"
                                className="text-slate hover:text-red-700 text-xs"
                                onClick={() => removeLine(line.id)}
                                title="Remove row"
                              >
                                ×
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ))}
                      <tr className="border-t border-black/10 bg-mist/40 font-medium">
                        <td className="px-2 py-2 sticky left-0 bg-mist/40 z-10" colSpan={2}>
                          Day totals
                        </td>
                        {days.map((d) => (
                          <td key={d} className="px-1 py-2 text-center text-navy">
                            {dayTotal(selectedLines, d) || ""}
                          </td>
                        ))}
                        <td className="px-2 py-2 text-right text-navy">{total}</td>
                        {editable ? <td /> : null}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-wrap gap-2 items-center">
                  {editable && (
                    <button
                      type="button"
                      className="px-3 py-2 text-sm border border-black/10 text-navy"
                      onClick={() => setPickingCode(true)}
                    >
                      Add row
                    </button>
                  )}
                  {editable && (
                    <button
                      type="button"
                      className="px-4 py-2 text-sm bg-navy text-white"
                      onClick={() => setShowSubmit(true)}
                    >
                      Submit
                    </button>
                  )}
                  {selected && canRequestCorrection(selected.status) && (
                    <button
                      type="button"
                      className="px-3 py-2 text-sm border border-gold/50 text-navy"
                      onClick={() => setCorrectionOpen(true)}
                    >
                      Request correction
                    </button>
                  )}
                  <span className="text-xs text-slate ml-auto">
                    Autosaves on entry · 0.25 hr increments
                  </span>
                </div>
              </div>

              {(notes.filter((n) => n.timesheet_id === selected.id).length > 0 ||
                accessRequests.length > 0) && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="bg-white border border-black/5 p-3">
                    <h3 className="text-sm font-semibold text-navy mb-2">Change notes</h3>
                    <ul className="space-y-2 max-h-40 overflow-y-auto text-xs text-slate">
                      {notes
                        .filter((n) => n.timesheet_id === selected.id)
                        .map((n) => (
                          <li key={n.id} className="border-b border-black/5 pb-2">
                            <span className="text-navy font-medium">
                              {n.date}: {n.old_hours} → {n.new_hours}
                            </span>
                            <div>{n.note}</div>
                            <div className="text-[10px]">
                              {n.by} · {new Date(n.at).toLocaleString()}
                            </div>
                          </li>
                        ))}
                      {notes.filter((n) => n.timesheet_id === selected.id).length === 0 && (
                        <li>No change notes on this period yet.</li>
                      )}
                    </ul>
                  </div>
                  <div className="bg-white border border-black/5 p-3">
                    <h3 className="text-sm font-semibold text-navy mb-2">My access requests</h3>
                    {accessRequests.length === 0 ? (
                      <p className="text-xs text-slate">None yet.</p>
                    ) : (
                      <ul className="space-y-1 text-xs">
                        {accessRequests.map((r) => (
                          <li key={r.id}>
                            <button
                              type="button"
                              className="text-left hover:text-teal"
                              onClick={() => setSelectedAccessId(r.id)}
                            >
                              {r.code} · {r.status}
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {selectedAccess && (
                      <div className="mt-3 border-t border-black/5 pt-3">
                        <RequestRoutingPanel
                          title={`Access · ${selectedAccess.code}`}
                          steps={accessSteps}
                          sla={{
                            stepTitle: "Owner · Approve charge code",
                            ...slaForQueueStep("Owner", "Approve charge code"),
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showLaborHelp && <LaborTypeHelpPanel onClose={() => setShowLaborHelp(false)} />}
      {pickingCode && (
        <ChargeCodePicker
          codes={chargeCodes}
          employeeId={DEMO_EMPLOYEE.id}
          excludeCodes={selectedLines.map((l) => l.charge_code)}
          onSelectAuthorized={addAuthorizedCode}
          onRequestAccess={(c) => {
            setAccessTarget(c);
            setAccessReason("");
          }}
          onCancel={() => setPickingCode(false)}
        />
      )}
      {accessTarget && (
        <AccessRequestModal
          code={accessTarget}
          reason={accessReason}
          setReason={setAccessReason}
          onSubmit={submitAccessRequest}
          onCancel={() => setAccessTarget(null)}
        />
      )}
      {pendingChange && (
        <ChangeNoteModal
          date={pendingChange.date}
          oldHours={pendingChange.oldHours}
          newHours={pendingChange.newHours}
          note={changeNote}
          setNote={setChangeNote}
          onConfirm={() => {
            const late = needsLateEntryReason(policy, pendingChange.date, todayIso());
            applyHours(
              pendingChange.lineId,
              pendingChange.date,
              pendingChange.newHours,
              changeNote.trim() || (late ? "Late entry" : "Correction"),
              late ? "late_entry" : "line_revision",
            );
            setPendingChange(null);
            setChangeNote("");
          }}
          onCancel={() => {
            setPendingChange(null);
            setChangeNote("");
          }}
        />
      )}
      {showSubmit && (
        <SubmitAttestationModal
          total={total}
          required={required}
          onConfirm={doSubmit}
          onCancel={() => setShowSubmit(false)}
        />
      )}
      {approveTargetId && (
        <SupervisorAttestationModal
          employeeName={sheets.find((s) => s.id === approveTargetId)?.employee ?? "employee"}
          onConfirm={() => approveSheet(approveTargetId)}
          onCancel={() => setApproveTargetId(null)}
        />
      )}
      {showRevisions && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4">
          <div className="bg-white max-w-lg w-full border border-black/10 shadow-lg p-5 space-y-3 max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-start gap-2">
              <h2 className="font-display text-xl text-navy">Correction history</h2>
              <button type="button" className="text-sm text-slate" onClick={() => setShowRevisions(false)}>
                Close
              </button>
            </div>
            <ul className="overflow-y-auto flex-1 divide-y divide-black/5 text-sm">
              {selectedNotes.length === 0 && (
                <li className="py-6 text-slate text-center">No revisions yet for this timesheet.</li>
              )}
              {selectedNotes.map((n) => (
                <li key={n.id} className="py-2">
                  <div className="font-medium text-navy">
                    {n.date}: {n.old_hours} → {n.new_hours}
                  </div>
                  <div className="text-xs text-slate">
                    {n.by} · {new Date(n.at).toLocaleString()}
                    {n.reason_code ? ` · ${n.reason_code}` : ""}
                  </div>
                  <div className="text-slate mt-0.5">{n.note}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {correctionOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4">
          <form
            className="bg-white max-w-md w-full border border-black/10 p-5 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              requestCorrection();
            }}
          >
            <h2 className="font-display text-xl text-navy">Request correction</h2>
            <p className="text-sm text-slate">
              Submitted and approved timesheets are locked. A correction reopens the sheet as an
              editable draft for resubmission.
            </p>
            <label className="block text-sm font-medium text-navy">
              Reason
              <textarea
                className="mt-1 w-full border border-black/10 px-3 py-2 min-h-[90px] text-sm"
                value={correctionReason}
                onChange={(e) => setCorrectionReason(e.target.value)}
                required
              />
            </label>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="px-3 py-2 text-sm border border-black/10"
                onClick={() => setCorrectionOpen(false)}
              >
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 text-sm bg-navy text-white">
                Open for correction
              </button>
            </div>
          </form>
        </div>
      )}
      {rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 p-4">
          <form
            className="bg-white max-w-md w-full border border-black/10 p-5 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              rejectSheet(rejectTarget);
            }}
          >
            <h2 className="font-display text-xl text-navy">Reject timesheet</h2>
            <label className="block text-sm font-medium text-navy">
              Reason
              <textarea
                className="mt-1 w-full border border-black/10 px-3 py-2 min-h-[90px] text-sm"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                required
              />
            </label>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="px-3 py-2 text-sm border border-black/10"
                onClick={() => setRejectTarget(null)}
              >
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 text-sm bg-red-700 text-white">
                Reject
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
